import { useLayoutEffect, useState, type MutableRefObject } from 'react';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { sub, startOfDay } from 'date-fns';
import type { Query } from '@atlassian/react-resource-router';
import {
	DataDepotProviderIds,
	MAX_NUMBER_OF_PULL_REQUESTS_TO_QUERY,
	PULL_REQUESTS_TABLE_PAGE_SIZE,
	PULL_REQUESTS_UPDATED_FROM_NUMBER_OF_DAYS,
	VcsProviderIds,
	nonCriticalGqlErrors,
	nonReliabilityCriticalErrors,
	queryParamsToWatch,
} from './constants.tsx';
import { RepositoryTypes, type Repository, type SanitizedUrl } from './types.tsx';

export const isBitBucket = (repository: Repository) =>
	repository === RepositoryTypes.REPO_BITBUCKET;

export const isBitbucketProvider = (providerId: string): boolean =>
	providerId === VcsProviderIds.BITBUCKET ||
	providerId === DataDepotProviderIds.BITBUCKET_PROD ||
	providerId === DataDepotProviderIds.BITBUCKET_STG;

const shouldCountToReliability = (error: Error) => {
	const okayErrors = [...nonCriticalGqlErrors, ...nonReliabilityCriticalErrors];
	// @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"HTTP status [403] while retrieving Bitbucket repository." | "HTTP status [404] while retrieving Bitbucket repository." | "Failed to retrieve Bitbucket repository because the user wasn't authorized." | "HTTP status [429] while retrieving Bitbucket repository." | "The user does not have 'View Development Tools' permi...'.
	return !okayErrors.includes(error.message);
};

export const codeErrorBoundaryAttributes = (error: Error) => {
	if (error instanceof Error) {
		return {
			countToReliability: shouldCountToReliability(error),
		};
	}
	return {
		countToReliability: true,
	};
};

export const toSanitizedUrl = (url: string): SanitizedUrl => sanitizeUrl(url);

/**
 * Hook to watch an element for it's content overflowing width.
 * @param ref element to watch for width overflow
 * @param callback optional callback, called with a boolean value indicating if the element currently has width overflow
 * @returns boolean value indicating whether the element's content is overflowing
 */
export const useIsOverflowWidth = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ref: MutableRefObject<any>,
	callback?: (isOverflowing: boolean) => void,
) => {
	const [isOverflow, setIsOverflow] = useState(false);

	useLayoutEffect(() => {
		const { current } = ref;

		const trigger = () => {
			const hasOverflow = current.scrollWidth > current.clientWidth;

			setIsOverflow(hasOverflow);

			if (callback) callback(hasOverflow);
		};

		if (current) {
			if ('ResizeObserver' in window) {
				new ResizeObserver(trigger).observe(current);
			}

			trigger();
		}
	}, [callback, ref]);

	return isOverflow;
};

export const getPullRequestsUpdatedFromDate = () =>
	startOfDay(
		sub(new Date(), {
			days: PULL_REQUESTS_UPDATED_FROM_NUMBER_OF_DAYS,
		}),
	).toISOString();

export const getNumPullRequestsToFetch = (queryParams: Query) => {
	const hasFilterOrSortQueryParam = Object.entries(queryParams).some(
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		([key, value]) => (queryParamsToWatch as string[]).includes(key) && value !== '',
	);

	return hasFilterOrSortQueryParam
		? MAX_NUMBER_OF_PULL_REQUESTS_TO_QUERY
		: PULL_REQUESTS_TABLE_PAGE_SIZE;
};
